import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Checkbox,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Autocomplete,
  TextField,
} from '@mui/material';
import { GroupsOutlined as GroupsIcon, PersonAddAlt1Outlined as PersonAddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { apiGet, apiPatch, apiPost } from '../utils/api';
import { authaltFetch } from '../services/authaltApi';
import { exportActivityRollCallExcel } from '../utils/activityRollCallExport';

const ATMANTAN_GOLD = '#E4C984';

const ActivityRollCallView = ({ selectedDate, onActionsReady }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedActivityKey, setSelectedActivityKey] = useState(null);
  const [savingBookingId, setSavingBookingId] = useState(null);
  const [enrollSlotId, setEnrollSlotId] = useState(null);
  const [guestOptions, setGuestOptions] = useState([]);
  const [guestsLoading, setGuestsLoading] = useState(false);
  const [selectedWalkIn, setSelectedWalkIn] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  const loadSlots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet(`/api/activities/timetable/available?date=${selectedDate}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load activities');
      setSlots(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load roll call data');
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  const activityGroups = useMemo(() => {
    const map = new Map();
    slots.forEach((slot) => {
      const key = String(slot.activityId ?? slot.activityName);
      if (!map.has(key)) {
        map.set(key, {
          key,
          activityId: slot.activityId,
          activityName: slot.activityName || 'Unknown',
          slots: [],
        });
      }
      map.get(key).slots.push(slot);
    });
    return Array.from(map.values())
      .map((group) => ({
        ...group,
        slots: [...group.slots].sort((a, b) =>
          String(a.startTime || '').localeCompare(String(b.startTime || ''))
        ),
        totalEnrolled: group.slots.reduce(
          (sum, s) => sum + (s.enrolledGuests?.length || s.currentEnrollment || 0),
          0
        ),
        attendedCount: group.slots.reduce(
          (sum, s) => sum + (s.enrolledGuests || []).filter((g) => g.attended === true).length,
          0
        ),
      }))
      .sort((a, b) => a.activityName.localeCompare(b.activityName));
  }, [slots]);

  useEffect(() => {
    if (activityGroups.length === 0) {
      setSelectedActivityKey(null);
      return;
    }
    if (!selectedActivityKey || !activityGroups.some((g) => g.key === selectedActivityKey)) {
      setSelectedActivityKey(activityGroups[0].key);
    }
  }, [activityGroups, selectedActivityKey]);

  const selectedActivity = activityGroups.find((g) => g.key === selectedActivityKey);

  const handleExport = useCallback(() => {
    if (!selectedActivity) return;
    exportActivityRollCallExcel({
      activityName: selectedActivity.activityName,
      date: selectedDate,
      slots: selectedActivity.slots,
    });
  }, [selectedActivity, selectedDate]);

  useEffect(() => {
    onActionsReady?.({
      canExport: Boolean(selectedActivity) && !loading,
      exportExcel: handleExport,
      activityCount: activityGroups.length,
    });
  }, [selectedActivity, loading, activityGroups.length, handleExport, onActionsReady]);

  const updateGuestInSlots = (bookingId, patch) => {
    setSlots((prev) =>
      prev.map((slot) => ({
        ...slot,
        enrolledGuests: (slot.enrolledGuests || []).map((guest) =>
          guest.bookingId === bookingId ? { ...guest, ...patch } : guest
        ),
      }))
    );
  };

  const handleAttendanceToggle = async (guest, checked) => {
    const bookingId = guest.bookingId;
    if (!bookingId) return;

    const attended = checked ? true : null;
    const previous = guest.attended;

    updateGuestInSlots(bookingId, { attended });
    setSavingBookingId(bookingId);

    try {
      const res = await apiPatch(`/api/activities/timetable/attendance/${bookingId}`, { attended });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save attendance');
      updateGuestInSlots(bookingId, {
        attended: data.attended,
        attendedAt: data.attendedAt,
        attendedBy: data.attendedBy,
      });
    } catch (e) {
      updateGuestInSlots(bookingId, { attended: previous });
      enqueueSnackbar(e.message || 'Failed to save attendance', { variant: 'error' });
    } finally {
      setSavingBookingId(null);
    }
  };

  const loadGuestOptions = useCallback(async () => {
    if (guestOptions.length > 0 || guestsLoading) return;
    setGuestsLoading(true);
    try {
      const types = ['InHouse', 'TodaysArrival'];
      let allGuests = [];
      for (const type of types) {
        const res = await authaltFetch('/guestlistdatav3', { type, index: 'atmantan' });
        if (!res.ok) continue;
        const response = await res.json();
        const categoryKey = type === 'InHouse' ? 'inHouse' : 'todaysArrival';
        allGuests = allGuests.concat(response[categoryKey]?.data || []);
      }
      const unique = Array.from(
        new Map(allGuests.map((g) => [g.guestId || g.id || g.GuestId, g])).values()
      );
      const list = unique
        .map((g) => ({
          id: String(g.guestId || g.id || g.GuestId || '').trim(),
          name: g.name || g.guestName || [g.firstName, g.lastName].filter(Boolean).join(' ') || '',
          room: g.room || g.roomNo || g.location || '',
        }))
        .filter((g) => g.id && g.name)
        .sort((a, b) => a.name.localeCompare(b.name));
      setGuestOptions(list);
    } catch (e) {
      enqueueSnackbar(e.message || 'Could not load guests', { variant: 'error' });
    } finally {
      setGuestsLoading(false);
    }
  }, [guestOptions.length, guestsLoading, enqueueSnackbar]);

  const openWalkIn = (slotId) => {
    setEnrollSlotId(slotId);
    setSelectedWalkIn(null);
    loadGuestOptions();
  };

  const handleWalkInEnroll = async (slot) => {
    if (!selectedWalkIn?.id || !slot?.id) return;
    setEnrolling(true);
    try {
      const res = await apiPost('/api/activities/timetable/enroll', {
        slotId: slot.id,
        guestId: selectedWalkIn.id,
        guestName: selectedWalkIn.name,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not enroll this guest');
      setEnrollSlotId(null);
      setSelectedWalkIn(null);
      await loadSlots();
      enqueueSnackbar(`${selectedWalkIn.name} enrolled as a walk-in`, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e.message || 'Could not enroll this guest', { variant: 'error' });
    } finally {
      setEnrolling(false);
    }
  };

  const handleMarkAllPresent = async (slot) => {
    if (!slot.id) return;
    try {
      const res = await apiPost('/api/activities/timetable/attendance/mark-all', {
        slotId: slot.id,
        attended: true,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to mark all present');
      await loadSlots();
      enqueueSnackbar(`Marked ${data.updated || 0} guest(s) present`, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(e.message || 'Failed to mark all present', { variant: 'error' });
    }
  };

  return (
    <Paper
      sx={{
        flex: 1,
        display: 'flex',
        minHeight: 420,
        overflow: 'hidden',
        bgcolor: '#fff',
        border: '1px solid #e5e7eb',
        boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
      }}
    >
      {loading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={32} sx={{ color: ATMANTAN_GOLD }} />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3, color: 'error.main' }}>{error}</Box>
      ) : activityGroups.length === 0 ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
          <Typography color="text.secondary">
            No scheduled activities with slots on this date.
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              width: 260,
              borderRight: '1px solid #e5e7eb',
              overflow: 'auto',
              flexShrink: 0,
              bgcolor: '#fafafa',
            }}
          >
            <Typography variant="subtitle2" sx={{ px: 2, py: 1.5, fontWeight: 600, color: '#666' }}>
              Activities
            </Typography>
            <List dense disablePadding>
              {activityGroups.map((group) => (
                <ListItemButton
                  key={group.key}
                  selected={group.key === selectedActivityKey}
                  onClick={() => setSelectedActivityKey(group.key)}
                  sx={{
                    borderLeft: group.key === selectedActivityKey ? `3px solid ${ATMANTAN_GOLD}` : '3px solid transparent',
                  }}
                >
                  <ListItemText
                    primary={group.activityName}
                    secondary={`${group.slots.length} slot${group.slots.length !== 1 ? 's' : ''} · ${group.totalEnrolled} enrolled · ${group.attendedCount} present`}
                    primaryTypographyProps={{ fontWeight: group.key === selectedActivityKey ? 600 : 400 }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {selectedActivity && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <GroupsIcon sx={{ color: ATMANTAN_GOLD }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedActivity.activityName}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${selectedActivity.slots.length} time slot${selectedActivity.slots.length !== 1 ? 's' : ''}`}
                    sx={{ bgcolor: '#f6efd8', color: '#171717' }}
                  />
                  <Chip
                    size="small"
                    label={`${selectedActivity.attendedCount} / ${selectedActivity.totalEnrolled} present`}
                    sx={{ bgcolor: '#ecfdf5', color: '#065f46' }}
                  />
                </Box>

                {selectedActivity.slots.map((slot, idx) => {
                  const guests = slot.enrolledGuests || [];
                  const instructors = (slot.instructorNames || [])
                    .filter(Boolean)
                    .join(', ') || slot.instructorName || '—';
                  const presentCount = guests.filter((g) => g.attended === true).length;
                  const capacity = Number(slot.maxCapacity) || 0;
                  const enrolledCount = Number(slot.currentEnrollment ?? guests.length);
                  const slotFull = capacity > 0 && enrolledCount >= capacity;
                  const enrolledIds = new Set(guests.map((g) => String(g.guestId || '')));
                  const walkInChoices = guestOptions.filter((g) => !enrolledIds.has(g.id));

                  return (
                    <Box key={slot.id || idx} sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {slot.startTime} – {slot.endTime}
                        </Typography>
                        <Chip size="small" label={instructors} variant="outlined" />
                        <Chip size="small" label={slot.roomName || 'No room'} variant="outlined" />
                        <Chip
                          size="small"
                          label={`${presentCount} / ${guests.length} present`}
                          sx={{ bgcolor: guests.length > 0 ? '#ecfdf5' : '#f3f4f6' }}
                        />
                        <Chip
                          size="small"
                          label={capacity ? `${enrolledCount} / ${capacity} enrolled` : `${enrolledCount} enrolled`}
                          variant="outlined"
                        />
                        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                          {guests.length > 0 && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleMarkAllPresent(slot)}
                              sx={{ textTransform: 'none' }}
                            >
                              Mark all present
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PersonAddIcon />}
                            disabled={slotFull}
                            onClick={() => openWalkIn(slot.id)}
                            sx={{
                              textTransform: 'none',
                              bgcolor: ATMANTAN_GOLD,
                              color: '#171717',
                              '&:hover': { bgcolor: '#d4b974' },
                            }}
                          >
                            {slotFull ? 'Slot full' : 'Enroll walk-in'}
                          </Button>
                        </Box>
                      </Box>

                      {enrollSlotId === slot.id && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                          <Autocomplete
                            size="small"
                            sx={{ minWidth: 280, flex: 1 }}
                            options={walkInChoices}
                            loading={guestsLoading}
                            value={selectedWalkIn}
                            onChange={(_, value) => setSelectedWalkIn(value)}
                            getOptionLabel={(option) =>
                              [option.name, option.id, option.room ? `Room ${option.room}` : ''].filter(Boolean).join(' · ')
                            }
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="In-house guest"
                                placeholder="Search name, ID, or room"
                              />
                            )}
                          />
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!selectedWalkIn || enrolling}
                            onClick={() => handleWalkInEnroll(slot)}
                            sx={{
                              textTransform: 'none',
                              bgcolor: '#171717',
                              color: '#fff',
                              '&:hover': { bgcolor: '#333', color: '#fff' },
                            }}
                          >
                            {enrolling ? 'Enrolling…' : 'Enroll'}
                          </Button>
                          <Button size="small" onClick={() => setEnrollSlotId(null)} sx={{ textTransform: 'none' }}>
                            Cancel
                          </Button>
                        </Box>
                      )}

                      {guests.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                          No guests enrolled for this slot.
                        </Typography>
                      ) : (
                        <Table size="small" sx={{ border: '1px solid #e5e7eb', borderRadius: 1 }}>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f9fafb' }}>
                              <TableCell sx={{ fontWeight: 600, width: 72 }}>Present</TableCell>
                              <TableCell sx={{ fontWeight: 600, width: 48 }}>#</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Guest Name</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Guest ID</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {guests.map((guest, i) => (
                              <TableRow key={guest.bookingId || `${guest.guestId}-${i}`} hover>
                                <TableCell>
                                  <Checkbox
                                    size="small"
                                    checked={guest.attended === true}
                                    disabled={savingBookingId === guest.bookingId}
                                    onChange={(e) => handleAttendanceToggle(guest, e.target.checked)}
                                    sx={{
                                      color: ATMANTAN_GOLD,
                                      '&.Mui-checked': { color: ATMANTAN_GOLD },
                                    }}
                                  />
                                </TableCell>
                                <TableCell>{i + 1}</TableCell>
                                <TableCell>
                                  {guest.guestName || '—'}
                                  {guest.enrollmentSource === 'walk_in' && (
                                    <Chip
                                      size="small"
                                      label="Walk-in"
                                      sx={{ ml: 1, height: 20, bgcolor: '#f6efd8', color: '#171717' }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell>{guest.guestId || '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                      {idx < selectedActivity.slots.length - 1 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                  );
                })}
              </>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

export default ActivityRollCallView;
